import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import TextField from '@mui/material/TextField'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import moment from 'moment-timezone'

const TIMEZONE = moment.tz.guess() // This should be set by user configuration

const dateRangeTypeItems = [
  {
    id: 'dynamic',
    name: 'Dynamic',
  },
  {
    id: 'fixed',
    name: 'Fixed',
  },
]

const resolutionItems = [
  {
    id: 'day',
    name: 'Daily',
    value: 0,
  },
  {
    id: 'week',
    name: 'Weekly',
    value: 1,
  },
  {
    id: 'month',
    name: 'Monthly',
    value: 2,
  },
  {
    id: 'quarter',
    name: 'Quarterly',
    value: 3,
  },
  {
    id: 'year',
    name: 'Yearly',
    value: 4,
  },
]

const EMPTY_SCORECARD_DATA = {
  name: '',
  description: '',
  resolution: 0,
  dateRangeType: 'dynamic',
  dateRangeDynamicDays: 90,
  dateRangeFixedStartDate: null,
  dateRangeFixedEndDate: null,
}

const ScorecardDialog = ({
  open,
  onClose,
  editId,
  scorecard,
  startDate,
  endDate,
  resolution,
  onScorecardSave,
}) => {
  const [scorecardData, setScorecardData] = useState(() => {
    if (editId) {
      return {
        ...EMPTY_SCORECARD_DATA,
        name: scorecard.name,
        description: scorecard.description,
        resolution: resolution,
        dateRangeType: scorecard.dateRangeType,
        dateRangeDynamicDays: scorecard.dateRangeDynamicDays,
        dateRangeFixedStartDate: startDate.format('YYYY-MM-DD'),
        dateRangeFixedEndDate: endDate.format('YYYY-MM-DD'),
      }
    } else {
      return {
        ...EMPTY_SCORECARD_DATA,
        dateRangeDynamicDays: startDate && endDate ? moment(endDate).diff(startDate, 'days') + 1 : 90,
        dateRangeFixedStartDate: startDate ? startDate.format('YYYY-MM-DD') : null,
        dateRangeFixedEndDate: endDate ? endDate.format('YYYY-MM-DD') : null,
      }
    }
  })

  const handleChange = (event) => {
    setScorecardData({ ...scorecardData, [event.target.name]: event.target.value })
  }

  const handleDateChange = (date, name) => {
    setScorecardData({ ...scorecardData, [name]: date.format('YYYY-MM-DD') })
  }

  const handleClose = (event, reason) => {
    if (reason === 'backdropClick') return
    onClose()
  }

  const handleSubmit = (editId=null, saveAs=false) => {
    if (editId) {
      // Update the scorecard or save as a new scorecard
      onScorecardSave(saveAs ? null : editId, scorecardData, saveAs)
    } else {
      // Create a new scorecard
      onScorecardSave(null, scorecardData)
    }
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='xs' fullWidth>
      <DialogTitle>{editId ? 'Update Scorecard' : 'Create New Scorecard'}</DialogTitle>
      <DialogContent>
        <Box display='flex' flexDirection='column' gap={2} paddingTop={1}>
          <TextField
            name='name'
            label='Name'
            value={scorecardData.name}
            onChange={handleChange}
            size='small'
            fullWidth
            required
          />
          <TextField
            name='description'
            label='Description'
            value={scorecardData.description}
            onChange={handleChange}
            size='small'
            fullWidth
          />
          <FormControl size='small' fullWidth required>
            <InputLabel id='resolutionLabel'>Frequency</InputLabel>
            <Select
              name='resolution'
              label='Frequency'
              value={scorecardData.resolution}
              onChange={handleChange}
              fullWidth
            >
              {resolutionItems.map(item => (
                <MenuItem key={item.id} value={item.value}>{item.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size='small' fullWidth required>
            <InputLabel id='dateRangeTypeLabel'>Date Range Type</InputLabel>
            <Select
              name='dateRangeType'
              label='Date Range Type'
              value={scorecardData.dateRangeType}
              onChange={handleChange}
              fullWidth
            >
              {dateRangeTypeItems.map(item => (
                <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {scorecardData.dateRangeType === 'dynamic' && (
            <TextField
              name='dateRangeDynamicDays'
              label='Last X Days'
              value={scorecardData.dateRangeDynamicDays}
              onChange={handleChange}
              size='small'
              fullWidth
            />
          )}
          {scorecardData.dateRangeType === 'fixed' && (
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                label='Start Date'
                value={moment(scorecardData.dateRangeFixedStartDate)}
                timezone={TIMEZONE}
                onChange={(date) => handleDateChange(date, 'dateRangeFixedStartDate')}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                  }
                }}
              />
              <DatePicker
                label='End Date'
                value={moment(scorecardData.dateRangeFixedEndDate)}
                timezone={TIMEZONE}
                onChange={(date) => handleDateChange(date, 'dateRangeFixedEndDate')}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                  }
                }}
              />
            </LocalizationProvider>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {editId && (
          <Button
            onClick={() => handleSubmit(editId, true)}
            color='primary'
            disabled={!scorecardData.name}
          >
            Save New
          </Button>
        )}

        <div style={{ flexGrow: 1 }} />

        <Button
          onClick={onClose}
          color='error'
        >
          Cancel
        </Button>
        <Button
          onClick={() => handleSubmit(editId)}
          color='secondary'
          disabled={!scorecardData.name}
        >
          {editId ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

ScorecardDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  editId: PropTypes.string,
  scorecard: PropTypes.object,
  startDate: PropTypes.object,
  endDate: PropTypes.object,
  onScorecardSave: PropTypes.func.isRequired,
}

export default ScorecardDialog